import React, { useEffect, useState, useCallback } from "react";
import {
  List,
  ListItemButton,
  ListItemText,
  Box,
  Divider,
  ListItemIcon,
  Drawer,
  Tooltip,
  IconButton,
  Typography,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  Alert,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import MenuIcon from "@mui/icons-material/Menu";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import BusinessIcon from "@mui/icons-material/Business";

import { Team, User, Users } from "../models/API";
import ChatBoxComponent from "./ChatBoxComponent";
import HelpCenterComponent from "./HelpCenterComponent";
import AdminConsoleComponent from "./AdminConsoleComponent";
import ReportComponent from "./ReportComponent";
import DataSourcesComponent from "./DataSourcesComponent";
import { useGlobal } from "../context/GlobalContext";
import KnowledgeComponent from "./KnowledgeComponent";
import WBRCalloutsViewer from "./WBRCalloutsViewer";
import SharedConversationPreview from "./SharedConversationPreview";
import DashboardComponent from "./dashboard/DashboardComponent";
import SettingsComponent from "./SettingsComponent";
import RequestAccessDialog from "./RequestAccessDialog";
import ConversationHistoryPanel from "./ConversationHistoryPanel";
import { generateAuditedClient } from "../audit/auditedClient";
import {
  getTeams as getTeamsQuery,
  getTeamsWithPermissions,
} from "../graphql/queries";
import { GetTeamsByUserNameInput } from "../models/API";
import { parseUserAttributes, getEffectiveRole } from "./utils/userAttributes";
import { fetchTeamLevelRole } from "./utils/checkTeamUserPermission";
import { setAuditTeam } from "../audit/auditContextStore";

const DRAWER_WIDTH = 220;
const COLLAPSED_WIDTH = 64;

type NavItem = { label: string; icon: React.ReactNode };
const chatBoxMenu = "Ask AI";
const reportMenu = "Report";
const dashboardMenu = "Dashboard";
const dataSourcesMenu = "Data Sources";
const knowledgeMenu = "Knowledge";
const wbrCalloutsMenu = "WBR Callouts";
const settingsMenu = "Settings";
const adminConsoleMenu = "Admin Console";
// NOTE: the Audit Console is no longer a rail item — it lives inside the
// Dashboard section as the superuser-gated `audit-console` dashboard entry
// (see dashboard/dashboardRegistry.ts visibleDashboardsFor).
const helpMenu = "Help";
// Full-page deep-link view for ?view=shared&token=... It has no rail menu
// item; it mounts only when the share URL is present (share-link spec).
const sharedPreviewMenu = "Shared Conversation";

const navItems: NavItem[] = [
  { label: dashboardMenu, icon: <DashboardOutlinedIcon /> },
  { label: chatBoxMenu, icon: <ChatBubbleOutlineIcon /> },
  { label: reportMenu, icon: <AssessmentOutlinedIcon /> },
  { label: dataSourcesMenu, icon: <StorageOutlinedIcon /> },
  { label: knowledgeMenu, icon: <MenuBookOutlinedIcon /> },
  { label: wbrCalloutsMenu, icon: <InsightsOutlinedIcon /> },
  { label: settingsMenu, icon: <SettingsOutlinedIcon /> },
  { label: adminConsoleMenu, icon: <AdminPanelSettingsOutlinedIcon /> },
  { label: helpMenu, icon: <HelpOutlineOutlinedIcon /> },
];

// Visibility rules for the nav rail, extracted so the rail render AND the
// default-active-view selection share one source of truth: a gated view
// (e.g. Admin Console for non-admins) must never be rendered OR selected as
// the landing view.
function isNavItemVisibleFor(
  label: string,
  ctx: {
    isAdmin: boolean;
    isAlpha: boolean;
    isVeslTeam: boolean;
  }
): boolean {
  if (label === adminConsoleMenu) return ctx.isAdmin;
  if (label === reportMenu) return ctx.isAlpha;
  if (label === wbrCalloutsMenu) return ctx.isVeslTeam;
  // The Dashboard rail item is shown to every team so Published Reports is
  // reachable from Brief (VESL) teams too (publish-report spec, Req 8). The
  // SPB Analytics content gate those teams used to hit here has moved into the
  // dashboard registry (`hideSpbAnalytics`), so the rule itself is unchanged.
  return true;
}

interface MainPageProps {
  currentUserName: string;
  userConfig: User | null;
  defaultTeamId: number;
  defaultTeamConfig: Team;
}

const MainPage: React.FC<MainPageProps> = ({
  currentUserName,
  userConfig,
  defaultTeamId,
  defaultTeamConfig,
}) => {
  const [teamId, setTeamId] = useState(defaultTeamId);
  const [teamConfig, setTeamConfig] = useState<Team>(defaultTeamConfig);
  const [teams, setTeams] = useState<Team[]>([defaultTeamConfig]);
  const [effectiveRole, setEffectiveRole] = useState<string>("user");
  // When a user opens a shareable link to a team they don't belong to
  // (e.g. ?team=SE), track the requested team name so we can show a
  // friendly "no access" banner instead of silently loading the default team.
  const [deniedTeamName, setDeniedTeamName] = useState<string | null>(null);
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [showRequestAccessDialog, setShowRequestAccessDialog] =
    useState<boolean>(false);

  // Ask AI conversation selection. Each login starts with a fresh "New Chat"
  // slot using the login_times-derived id (keeps existing history lookups
  // consistent). Whenever the user posts their first message in the pinned
  // New Chat row, we rotate to a fresh id so the current session graduates
  // to a regular history entry and a new empty slot appears on top.
  const defaultConversationId = userConfig!.login_times.toString();
  const [activeConversationId, setActiveConversationId] = useState<string>(
    defaultConversationId
  );
  const [newChatConversationId, setNewChatConversationId] = useState<string>(
    defaultConversationId
  );
  const [conversationRefreshKey, setConversationRefreshKey] =
    useState<number>(0);
  const refreshConversationHistory = useCallback(() => {
    setConversationRefreshKey((k) => k + 1);
    // If the user just asked a question inside the pinned New Chat slot,
    // graduate that session and mint a fresh id for the pinned row. The
    // active conversation id stays put so the chat continues uninterrupted.
    setNewChatConversationId((currentNewChatId) => {
      if (activeConversationId === currentNewChatId) {
        return Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
      }
      return currentNewChatId;
    });
  }, [activeConversationId]);

  // After "Add to my conversations" succeeds in the shared preview, strip the
  // ?view=shared&token=... params, select the new Copy, and land on Ask AI so
  // ChatBoxComponent loads the copied turns from the user's own history.
  const handleSharedConversationAdded = useCallback(
    (conversationId: string) => {
      window.history.replaceState({}, "", window.location.pathname);
      setActiveConversationId(conversationId);
      setActiveItem(chatBoxMenu);
      setConversationRefreshKey((k) => k + 1);
    },
    []
  );

  const { darkMode, setDarkMode } = useGlobal();
  const muiTheme = React.useMemo(
    () => createTheme({ palette: { mode: darkMode ? "dark" : "light" } }),
    [darkMode]
  );
  const theme = useTheme();
  const userAttributes = React.useMemo(
    () => parseUserAttributes(userConfig),
    [userConfig]
  );
  const { globalTeamId, setGlobalTeamId } = useGlobal();

  useEffect(() => {
    if (userConfig && typeof userAttributes.dark_mode === "boolean") {
      setDarkMode(userAttributes.dark_mode);
    }
  }, [userConfig, userAttributes.dark_mode, setDarkMode]);

  // Reorder navItems based on user preferences
  const orderedNavItems = React.useMemo(() => {
    if (!userAttributes.menu_order || userAttributes.menu_order.length === 0) {
      return navItems;
    }
    // Create a map of label to navItem for quick lookup
    const itemMap = new Map(navItems.map((item) => [item.label, item]));
    // Reorder based on user preferences
    const ordered = userAttributes.menu_order
      .map((label) => itemMap.get(label))
      .filter((item): item is NavItem => item !== undefined);
    // Add any items that weren't in the user's order (in case new items were added)
    // Prepend them so new features are visible at the top, not buried at the bottom.
    const missing: NavItem[] = [];
    navItems.forEach((item) => {
      if (!userAttributes.menu_order!.includes(item.label)) {
        missing.push(item);
      }
    });
    return [...missing, ...ordered];
  }, [userAttributes.menu_order]);

  // Team-derived flags. Hoisted here (right after orderedNavItems) so they're
  // in scope for the activeItem initializer, the visibleNavItems memo, and the
  // fallback effect — the default/active view must obey the same visibility
  // rules as the rail. They depend only on teamConfig state.
  // Get current team name
  const currentTeamName = teamConfig?.name || "No Team";

  // Check if current team is a VESL team
  const isVeslTeam = currentTeamName.startsWith("VESL - ");

  // VESL teams with SP, SB, or SPB in their name get the dashboard.
  // Split the name after "VESL - " into tokens by spaces/hyphens/underscores.
  // A token qualifies if it:
  //   - equals SP, SB, or SPB exactly (case-insensitive), OR
  //   - starts with SP/SB/SPB followed by a CamelCase boundary
  //     (uppercase letter then lowercase, e.g. "SPAdvGrowth" = SP + Adv...)
  // This excludes all-caps words like "SPOT" where SP is coincidental.
  const isVeslDashboardTeam = (() => {
    if (!isVeslTeam) return false;
    const suffix = currentTeamName.replace(/^VESL\s*-\s*/i, "");
    const tokens = suffix.split(/[\s\-_]+/);
    return tokens.some(
      (t) => /^(SPB|SB|SP)$/i.test(t) || /^(SPB|SB|SP)[A-Z][a-z]/.test(t)
    );
  })();

  // Set active item — honour ?view= URL param for deep-linking, then VESL default, then first menu item
  const isDefaultVeslTeam = (defaultTeamConfig?.name || "").startsWith(
    "VESL - "
  );
  const [activeItem, setActiveItem] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const viewParam = params.get("view");
    if (viewParam) {
      // Shared-conversation preview is a full-page view with no menu item.
      if (viewParam.toLowerCase() === "shared") {
        return sharedPreviewMenu;
      }
      const viewMap: Record<string, string> = {
        "ask-ai": chatBoxMenu,
        chat: chatBoxMenu,
        report: reportMenu,
        dashboard: dashboardMenu,
        "data-sources": dataSourcesMenu,
        knowledge: knowledgeMenu,
        "wbr-callouts": wbrCalloutsMenu,
        settings: settingsMenu,
        admin: adminConsoleMenu,
        help: helpMenu,
      };
      const mapped = viewMap[viewParam.toLowerCase()];
      // WBR Callouts is hidden for non-VESL teams; ignore the deep-link
      // so we don't render the viewer (which would rewrite the URL).
      if (mapped && (mapped !== wbrCalloutsMenu || isDefaultVeslTeam)) {
        return mapped;
      }
    }
    // VESL teams default to WBR Callouts
    if (isDefaultVeslTeam) return wbrCalloutsMenu;
    // Non-VESL teams: skip WBR Callouts in the user's custom menu order,
    // since it's hidden for them and would otherwise mount the viewer
    // (which writes ?view=wbr-callouts back into the URL). Also skip any gated
    // view (e.g. Admin Console for non-admins) using the same rules as the
    // rail, so the landing view is never a hidden one. Mount-time context:
    // env isn't destructured yet here, so derive alpha from the URL.
    const initialVisibilityCtx = {
      isAdmin: (userAttributes.role || "user") === "admin",
      isAlpha: window.location.origin.includes("alpha"),
      isVeslTeam,
    };
    const firstVisible = orderedNavItems.find(
      (item) =>
        item.label !== wbrCalloutsMenu &&
        isNavItemVisibleFor(item.label, initialVisibilityCtx)
    );
    return firstVisible?.label || chatBoxMenu;
  });

  const [railVisible, setRailVisible] = React.useState<boolean>(false);
  const [railExpanded, setRailExpanded] = React.useState<boolean>(false);
  const railWidth = railExpanded ? DRAWER_WIDTH : COLLAPSED_WIDTH;
  const effectiveRailWidth = railVisible
    ? railExpanded
      ? DRAWER_WIDTH
      : COLLAPSED_WIDTH
    : 0;
  const { setEnv, env } = useGlobal();

  if (window.location.origin.includes("prod")) {
    setEnv("prod");
  } else if (window.location.origin.includes("gamma")) {
    setEnv("gamma");
  } else if (window.location.origin.includes("alpha")) {
    setEnv("alpha");
  }

  // Single source of truth for which nav items are visible, using the live
  // context (role/env/team). The rail renders these, and the fallback effect
  // resets to the first of these — so a gated view is never shown or selected.
  const visibleNavItems = React.useMemo(
    () =>
      orderedNavItems.filter((item) =>
        isNavItemVisibleFor(item.label, {
          isAdmin: effectiveRole === "admin",
          isAlpha: env === "alpha",
          isVeslTeam,
        })
      ),
    [orderedNavItems, effectiveRole, env, isVeslTeam]
  );

  const getTeams = useCallback(async () => {
    try {
      const client = generateAuditedClient();
      const input: GetTeamsByUserNameInput = {
        user_name: currentUserName,
      };
      const result = await client.graphql({
        query: getTeamsWithPermissions,
        variables: {
          input: input,
        },
      });
      const teamsData = result.data.getTeamsWithPermissions;
      setTeams(teamsData);

      // Deep-link: if ?team= is in the URL, select that team instead of default
      const params = new URLSearchParams(window.location.search);
      const teamParam = params.get("team");
      let selectedTeam: Team | undefined;
      if (teamParam) {
        const paramLower = teamParam.toLowerCase();
        const veslPrefixed = `vesl - ${paramLower}`;
        selectedTeam = teamsData.find((team: Team) => {
          const nameLower = team.name.toLowerCase();
          return nameLower === paramLower || nameLower === veslPrefixed;
        });
        // User doesn't have access to the requested team — flag it so we can
        // surface a friendly message, and fall back to their default team.
        if (!selectedTeam) {
          setDeniedTeamName(teamParam);
        }
      }

      // Fall back to the default team if no URL param or no match
      if (!selectedTeam) {
        selectedTeam = teamsData.find((team: Team) => team.is_default === true);
      }

      if (selectedTeam) {
        setTeamId(selectedTeam.id);
        setGlobalTeamId(selectedTeam.id);
        setTeamConfig(selectedTeam);
        // Record the resolved team in the audit context store so subsequent
        // GraphQL operations carry the current team_id + name (Req 1.3).
        setAuditTeam(selectedTeam.id, selectedTeam.name);
      }
    } catch (err) {
      console.error(err);
    }
  }, [currentUserName, setGlobalTeamId]);

  useEffect(() => {
    getTeams();
  }, [getTeams]);

  // Fetch effective role: the team-level role is resolved SERVER-SIDE by the
  // getEffectiveTeamRole query (via fetchTeamLevelRole, design D9 of
  // .kiro/specs/finn-mcp-team-permissions) — MAX(role) over the caller's
  // direct team_user_map row and the Permissions-group rows the caller is a
  // BRASS-verified member of. This replaces the former inline
  // GetEffectiveRoleData query and its client-side group scan, which
  // recognised only the legacy row convention (clause 1.10) and over-granted
  // from group rows the caller was not a member of (R-5).
  useEffect(() => {
    const fetchEffectiveRole = async () => {
      try {
        if (!userConfig) return;

        const teamLevelRole = await fetchTeamLevelRole(
          currentUserName,
          globalTeamId
        );
        // null means "no direct row and no BRASS-verified group row". The old
        // inline scan composed that case as "user" (roleRank(null) = 1, so
        // highestRank over [directRole, ...groupRoles] never went below 1);
        // map null -> "user" so getEffectiveRole receives the same non-null
        // shape it always has.
        const teamRole = teamLevelRole ?? "user";

        const role = getEffectiveRole(userAttributes, { role: teamRole });
        setEffectiveRole(role);
      } catch (err) {
        console.error("Error fetching effective role:", err);
        setEffectiveRole(userAttributes.role || "user");
      }
    };

    fetchEffectiveRole();
  }, [globalTeamId, userConfig, userAttributes, currentUserName]);

  // Reset the active item to the first VISIBLE item whenever the current
  // selection isn't visible — e.g. a stale/gated selection, or the ordered
  // menu changed. Uses visibleNavItems so this shares the rail's rules and
  // can never leave a gated view (like Admin Console) selected. Declared after
  // visibleNavItems so the dependency reference resolves.
  useEffect(() => {
    if (
      visibleNavItems.length > 0 &&
      activeItem !== sharedPreviewMenu &&
      !visibleNavItems.some((item) => item.label === activeItem)
    ) {
      setActiveItem(visibleNavItems[0].label);
    }
  }, [visibleNavItems, activeItem]);

  const menuToSlug: Record<string, string> = {
    [dashboardMenu]: "dashboard",
    [chatBoxMenu]: "ask-ai",
    [reportMenu]: "report",
    [dataSourcesMenu]: "data-sources",
    [knowledgeMenu]: "knowledge",
    [wbrCalloutsMenu]: "wbr-callouts",
    [settingsMenu]: "settings",
    [adminConsoleMenu]: "admin",
    [helpMenu]: "help",
  };

  function handleMenuItemClick(itemName: string) {
    // When navigating away from the WBR Callouts viewer, strip the deep-link
    // params it wrote while it was active (week/team/view/report/merged/page/
    // tab/cellRefs/impact). Other menu items don't own any URL params, so a
    // clean pathname is the right state for them. Re-entering WBR Callouts
    // will re-write them from current viewer state via its URL-sync effect.
    if (activeItem === wbrCalloutsMenu && itemName !== wbrCalloutsMenu) {
      window.history.replaceState({}, "", window.location.pathname);
    }
    setActiveItem(itemName);

    // Update the URL with ?view=<slug> so the link is shareable / bookmarkable.
    // WBR Callouts manages its own URL params via its internal sync effect,
    // so we skip writing ?view= for it here.
    if (itemName !== wbrCalloutsMenu) {
      const slug = menuToSlug[itemName];
      if (slug) {
        const params = new URLSearchParams(window.location.search);
        params.set("view", slug);
        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}?${params.toString()}`
        );
      }
    }
  }

  // Handlers for "request access" flow when the shareable link references a
  // team the user isn't part of.
  const handleOpenRequestAccess = async () => {
    // Lazy-load the full team list so the dialog can show every team for
    // selection (the requested one may or may not be in this list).
    try {
      if (allTeams.length === 0) {
        const client = generateAuditedClient();
        const result = await client.graphql({ query: getTeamsQuery });
        setAllTeams(result.data.getTeams as Team[]);
      }
    } catch (err) {
      console.error("Failed to load teams for request access:", err);
    }
    setShowRequestAccessDialog(true);
  };

  const handleRequestAccessClose = () => {
    setShowRequestAccessDialog(false);
  };

  const handleRequestAccessSave = (_item: Users) => {
    // No-op: the mutation is handled inside RequestAccessDialog; we just
    // need to close the dialog and acknowledge the request.
    setShowRequestAccessDialog(false);
    setDeniedTeamName(null);
  };

  const handleDismissDeniedBanner = () => {
    // Strip the offending ?team= param so a page refresh doesn't keep
    // flashing the banner, while preserving the rest of the deep-link.
    const params = new URLSearchParams(window.location.search);
    params.delete("team");
    const qs = params.toString();
    const newUrl = qs
      ? `${window.location.pathname}?${qs}`
      : window.location.pathname;
    window.history.replaceState({}, "", newUrl);
    setDeniedTeamName(null);
  };

  const handleTeamChange = (event: SelectChangeEvent<number>) => {
    const selectedTeamId = event.target.value as number;
    setTeamId(selectedTeamId);
    setGlobalTeamId(selectedTeamId);

    // Update team config based on selected team
    const selectedTeam = teams.find((team) => team.id === selectedTeamId);
    if (selectedTeam) {
      setTeamConfig(selectedTeam);
      // Keep the audit context store in sync on a team switch so operations
      // issued afterward carry the newly selected team_id + name (Req 1.3).
      setAuditTeam(selectedTeam.id, selectedTeam.name);

      // If switching to a VESL team, default to WBR Callouts
      const switchingToVesl = selectedTeam.name.startsWith("VESL - ");
      const leavingVesl =
        teamConfig?.name?.startsWith("VESL - ") && !switchingToVesl;
      if (switchingToVesl) {
        setActiveItem(wbrCalloutsMenu);
      } else if (activeItem === wbrCalloutsMenu) {
        // Leaving VESL while on WBR Callouts: pick the first menu item that
        // isn't WBR Callouts. If we kept WBR Callouts (e.g. it's first in the
        // user's saved menu order), the viewer stays mounted with isOpen=true
        // and its URL-sync effect rewrites ?team=…&view=wbr-callouts using
        // the new non-VESL team name.
        const firstVisible = orderedNavItems.find(
          (item) => item.label !== wbrCalloutsMenu
        );
        setActiveItem(firstVisible?.label || chatBoxMenu);
      }
      // Leaving a VESL team should land on a clean home URL — strip the
      // WBR-callouts deep-link params (week/team/view/report/...) that the
      // viewer wrote while it was active.
      if (leavingVesl) {
        window.history.replaceState({}, "", window.location.pathname);
      }
    }
  };

  // Weblab Tracker dashboard visibility: the user must be a MEMBER of the
  // Weblab Tracker team (FINN team id 104), regardless of which team is
  // currently selected. `teams` is the user's own permission-scoped team
  // list (from getTeamsWithPermissions keyed by user_name), so membership
  // is a simple lookup by id.
  const WEBLAB_TRACKER_TEAM_ID = 104;
  const isWeblabTrackerMember = teams.some(
    (t) => t.id === WEBLAB_TRACKER_TEAM_ID
  );

  // Weblab Visual dashboard visibility: restricted to members of the SPB
  // Finance team (FINN team id 2). The dashboard's weblab-science source data
  // is directionally correct but not dollar-accurate, so it is scoped to SPB
  // Finance internal use (leadership decision). Same simple membership lookup
  // against the user's own permission-scoped `teams` list.
  const SPB_FINANCE_TEAM_ID = 2;
  const isSpbFinanceMember = teams.some((t) => t.id === SPB_FINANCE_TEAM_ID);

  // @ts-ignore
  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      {/* "You don't have access to this team" banner, shown only when the
          shareable link specified a ?team= value the user isn't a member of. */}
      {deniedTeamName && (
        <Alert
          severity="warning"
          onClose={handleDismissDeniedBanner}
          sx={{
            position: "fixed",
            top: 8,
            left: 60,
            right: 8,
            zIndex: theme.zIndex.tooltip + 1,
            boxShadow: 2,
          }}
          action={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={handleOpenRequestAccess}
              >
                Request Access
              </Button>
            </Box>
          }
        >
          <Typography variant="body2">
            You're not a member of the <strong>{deniedTeamName}</strong> team,
            so this shared link can't be opened. Showing your default team (
            {teamConfig?.name || "—"}) instead. Request access to see this
            team's data.
          </Typography>
        </Alert>
      )}
      <RequestAccessDialog
        isOpen={showRequestAccessDialog}
        onClose={handleRequestAccessClose}
        onSave={handleRequestAccessSave}
        currentUserId={userConfig?.id ?? 0}
        initialData={allTeams}
        currentUserName={currentUserName}
      />
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        <IconButton
          size="small"
          onClick={() => setRailVisible((v) => !v)}
          aria-label={railVisible ? "Hide menu" : "Show menu"}
          sx={{
            position: "fixed",
            top: 8,
            left: 8,
            zIndex: theme.zIndex.tooltip + 2,
            bgcolor: "background.paper",
            boxShadow: 2,
            "&:hover": { bgcolor: "background.paper" },
          }}
        >
          <MenuIcon fontSize="small" />
        </IconButton>

        <Box
          sx={{
            position: "fixed",
            left: 0,
            top: 0,
            height: "100vh",
            width: COLLAPSED_WIDTH,
            zIndex: theme.zIndex.drawer,
            transform: railVisible
              ? "translateX(0)"
              : `translateX(-${COLLAPSED_WIDTH}px)`,
            transition: theme.transitions.create("transform", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.standard,
            }),
            pointerEvents: railVisible ? "auto" : "none",
          }}
          onMouseEnter={() => railVisible && setRailExpanded(true)}
          onMouseLeave={() => railVisible && setRailExpanded(false)}
        >
          <Drawer
            variant="permanent"
            PaperProps={{
              elevation: 0,
              sx: {
                width: railWidth,
                overflowX: "hidden",
                transition: theme.transitions.create("width", {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.standard,
                }),
                borderRightColor: "divider",
                pt: 6,
                display: "flex",
                flexDirection: "column",
                backgroundColor: "background.paper",
              },
            }}
            sx={{
              "& .MuiDrawer-paper": {
                top: 0,
                left: 0,
                height: "100vh",
              },
            }}
            open
          >
            <Divider />
            {/*
              When Recents is shown below, the nav list yields the remaining
              space to it (flex 0). Otherwise it expands to push the team
              selector to the bottom.
            */}
            <List
              sx={{
                py: 0,
                flex:
                  activeItem === chatBoxMenu && railExpanded ? "0 0 auto" : 1,
              }}
            >
              {visibleNavItems.map((item) => {
                const showLabel = railExpanded;
                return (
                  <ListItemButton
                    selected={activeItem === item.label}
                    onClick={() => handleMenuItemClick(item.label)}
                    key={item.label}
                    sx={{
                      height: 48,
                      minHeight: 48,
                      px: 2,
                      justifyContent: "flex-start",
                      alignItems: "center",
                      borderRadius: 2,
                      mb: 0.5,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "action.hover",
                        transform: "translateX(2px)",
                      },
                      "&.Mui-selected": {
                        backgroundColor: "primary.50",
                        borderLeft: "3px solid",
                        borderLeftColor: "primary.main",
                        "&:hover": {
                          backgroundColor: "primary.100",
                        },
                      },
                    }}
                  >
                    <Tooltip
                      title={showLabel ? "" : item.label}
                      placement="right"
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          mr: 1.5,
                          justifyContent: "center",
                          color: "inherit",
                          display: "flex",
                          alignItems: "center",
                          height: 24,
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                    </Tooltip>

                    <Box
                      sx={{
                        flex: 1,
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <ListItemText
                        primary={item.label}
                        sx={{
                          opacity: showLabel ? 1 : 0,
                          transition: theme.transitions.create("opacity", {
                            duration: theme.transitions.duration.shortest,
                          }),
                        }}
                      />
                    </Box>
                  </ListItemButton>
                );
              })}
            </List>
            {/* Recents — only on Ask AI, only when the rail is expanded.
                Sits between the nav and the team selector at the bottom. */}
            {activeItem === chatBoxMenu && railExpanded && userConfig && (
              <>
                <Divider sx={{ mx: 1 }} />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    flex: "1 1 0",
                    overflow: "hidden",
                  }}
                >
                  <ConversationHistoryPanel
                    userId={userConfig.id}
                    currentConversationId={activeConversationId}
                    newChatConversationId={newChatConversationId}
                    onSelectConversation={setActiveConversationId}
                    refreshKey={conversationRefreshKey}
                    embedded
                  />
                </Box>
              </>
            )}
            {/* Team Selector at Bottom - Positioned Higher */}
            <Divider />
            <Box
              sx={{
                px: 2,
                py: 1.5,
                mb: 6,
                display: "flex",
                alignItems: "center",
                minHeight: 56,
                bgcolor: "background.default",
              }}
            >
              <Tooltip
                title={railExpanded ? "" : currentTeamName}
                placement="right"
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: railExpanded ? 1.5 : 0,
                    justifyContent: "center",
                    color: "text.secondary",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <BusinessIcon fontSize="small" />
                </ListItemIcon>
              </Tooltip>
              <Box
                sx={{
                  flex: 1,
                  overflow: "hidden",
                  opacity: railExpanded ? 1 : 0,
                  transition: theme.transitions.create("opacity", {
                    duration: theme.transitions.duration.shortest,
                  }),
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    fontSize: "0.7rem",
                    mb: 0.5,
                  }}
                >
                  Current Team
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={globalTeamId || teamId}
                    onChange={handleTeamChange}
                    sx={{
                      fontSize: "0.875rem",
                      "& .MuiSelect-select": {
                        py: 0.5,
                      },
                    }}
                  >
                    {teams.map((team) => (
                      <MenuItem key={team.id} value={team.id}>
                        {team.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Drawer>
        </Box>

        <ChatBoxComponent
          isOpen={activeItem === chatBoxMenu}
          currentUserName={currentUserName}
          userConfig={userConfig}
          currentTeamId={globalTeamId}
          teamConfig={teamConfig}
          conversationId={activeConversationId}
          menuWidth={effectiveRailWidth}
          onConversationSaved={refreshConversationHistory}
          effectiveRole={effectiveRole}
        />
        <ReportComponent
          isOpen={activeItem === reportMenu}
          menuWidth={effectiveRailWidth}
        />
        <DashboardComponent
          isOpen={activeItem === dashboardMenu}
          menuWidth={effectiveRailWidth}
          effectiveRole={effectiveRole}
          currentTeamId={globalTeamId}
          isWeblabTrackerMember={isWeblabTrackerMember}
          isSpbFinanceMember={isSpbFinanceMember}
          userId={userConfig?.id}
          userAttributes={userConfig?.attributes}
          darkMode={darkMode}
          onToggleDark={() => setDarkMode(!darkMode)}
          hideSpbAnalytics={isVeslTeam && !isVeslDashboardTeam}
        />
        <DataSourcesComponent
          isOpen={activeItem === dataSourcesMenu}
          menuWidth={effectiveRailWidth}
          currentTeamId={globalTeamId}
          currentUserName={currentUserName}
          userConfig={userConfig}
          effectiveRole={effectiveRole}
        />
        <KnowledgeComponent
          isOpen={activeItem === knowledgeMenu}
          menuWidth={effectiveRailWidth}
          currentTeamId={globalTeamId}
          currentUserName={currentUserName}
          userConfig={userConfig}
          effectiveRole={effectiveRole}
        />
        <WBRCalloutsViewer
          isOpen={activeItem === wbrCalloutsMenu}
          menuWidth={effectiveRailWidth}
          currentTeamId={globalTeamId}
          currentTeamName={teamConfig?.name || ""}
          currentUserName={currentUserName}
          userConfig={userConfig}
          effectiveRole={effectiveRole}
        />
        <SettingsComponent
          isOpen={activeItem === settingsMenu}
          currentUserName={currentUserName}
          userConfig={userConfig}
          currentTeamId={globalTeamId}
          teamConfig={teamConfig}
          menuWidth={effectiveRailWidth}
          effectiveRole={effectiveRole}
        />
        <AdminConsoleComponent
          isOpen={activeItem === adminConsoleMenu}
          menuWidth={effectiveRailWidth}
          currentTeamId={globalTeamId}
          currentUserName={currentUserName}
          effectiveRole={effectiveRole}
        />
        <HelpCenterComponent
          isOpen={activeItem === helpMenu}
          menuWidth={effectiveRailWidth}
        />
        <SharedConversationPreview
          isOpen={activeItem === sharedPreviewMenu}
          menuWidth={effectiveRailWidth}
          userId={userConfig?.id ?? 0}
          onConversationAdded={handleSharedConversationAdded}
        />
      </Box>
    </ThemeProvider>
  );
};

export default MainPage;
